import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { notificationKeys } from "./notifications.query-keys";
import { notificationsService } from "./notifications.service";
import type {
  MarkAllNotificationsSeenRequest,
  MarkAllNotificationsSeenResponse,
  MarkNotificationSeenRequest,
  Notification,
  NotificationCountParams,
  NotificationCountsResponse,
  NotificationFilters,
  NotificationListParams,
  NotificationListResponse,
} from "../types";

const PAGE_SIZE = 30;
const DEFAULT_POLL_INTERVAL = 10_000;
const PENDING_POLL_INTERVAL = 10_000;

interface NotificationQueryOptions {
  enabled?: boolean;
}

type ListData = InfiniteData<NotificationListResponse, number>;
type QuerySnapshot = [readonly unknown[], unknown];

function listHasPending(data: ListData | undefined): boolean {
  return Boolean(
    data?.pages.some((page) =>
      page.results.some((notification) => notification.async_task?.status === "Pending"),
    ),
  );
}

function notificationMatchesFilters(
  notification: Notification,
  filters: NotificationFilters,
): boolean {
  if (filters.is_seen !== undefined && notification.is_seen !== filters.is_seen) return false;
  if (filters.category) {
    const cats = Array.isArray(filters.category) ? filters.category : [filters.category];
    if (!cats.includes(notification.category)) return false;
  }
  if (filters.notable === true && !notification.notable) return false;
  if (filters.notable === false && notification.notable) return false;
  if (filters.board_id !== undefined && filters.board_id !== null && notification.board !== filters.board_id) return false;
  if (filters.async_status && filters.async_status.length > 0 && notification.async_task) {
    if (!filters.async_status.includes(notification.async_task.status)) return false;
  }
  if (filters.notification_status && filters.notification_status.length > 0) {
    if (!(filters.notification_status as string[]).includes(notification.status)) return false;
  }
  if (filters.search) {
    const q = filters.search.toLowerCase();
    const title = (notification.title ?? "").toLowerCase();
    const desc = (notification.description ?? "").toLowerCase();
    if (!title.includes(q) && !desc.includes(q)) return false;
  }
  return true;
}

function countsHavePending(data: NotificationCountsResponse | undefined): boolean {
  return Boolean(
    data?.status_counts.some(
      (count) =>
        count.notify_type === "async" &&
        count.status === "Pending" &&
        count.count > 0,
    ),
  );
}

function isListQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === notificationKeys.all[0] && queryKey[1] === "list";
}

function isCountsQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === notificationKeys.all[0] && queryKey[1] === "counts";
}

function isNotificationCacheQuery(queryKey: readonly unknown[]): boolean {
  return isListQuery(queryKey) || isCountsQuery(queryKey);
}

function snapshotNotificationQueries(): QuerySnapshot[] {
  return queryClient.getQueriesData({
    predicate: (query) => isNotificationCacheQuery(query.queryKey),
  });
}

function restoreSnapshots(snapshots: QuerySnapshot[] | undefined): void {
  snapshots?.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

/** Refetch active + inactive caches so My Swivu counts update after board mark-seen. */
function invalidateAllNotificationQueries() {
  return queryClient.invalidateQueries({
    queryKey: notificationKeys.all,
    refetchType: "all",
  });
}

function asCategoryList(value: string | string[] | undefined): string[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

/** Which inbox list caches a mark-all body can change. */
function listParamsAffectedByMarkAll(
  params: NotificationListParams,
  filters: NotificationFilters,
): boolean {
  if (filters.notable === true && filters.category == null) {
    // Notable clear — skip Routine-only lists.
    return params.notable !== false;
  }
  if (filters.category != null) {
    const marked = asCategoryList(filters.category);
    if (params.category != null) {
      return asCategoryList(params.category).some((c) => marked.includes(c));
    }
    // Notable inbox can include notables from that category.
    return params.notable === true;
  }
  return true;
}

function countsParamsAffectedByMarkAll(
  params: NotificationCountParams,
  filters: NotificationFilters,
): boolean {
  if (filters.notable === true && filters.category == null) {
    // Notable clear — Routine partition unread is unchanged.
    return params.notable !== false;
  }
  // Category clear can touch notables and routine in that category.
  return true;
}

/**
 * After mark-all: refresh affected counts (include inactive for badge / My Swivu)
 * and only list queries the mark-all body can change — not every cached inbox page.
 */
function invalidateAfterMarkAllSeen(filters: MarkAllNotificationsSeenRequest) {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      if (!isCountsQuery(query.queryKey)) return false;
      const params = (query.queryKey[2] ?? {}) as NotificationCountParams;
      return countsParamsAffectedByMarkAll(params, filters);
    },
    refetchType: "all",
  });
  void queryClient.invalidateQueries({
    predicate: (query) => {
      if (!isListQuery(query.queryKey)) return false;
      const params = (query.queryKey[2] ?? {}) as NotificationListParams;
      return listParamsAffectedByMarkAll(params, filters);
    },
  });
}

function findNotificationInLists(id: number): Notification | undefined {
  const lists = queryClient.getQueriesData<ListData>({
    predicate: (query) => isListQuery(query.queryKey),
  });
  for (const [, data] of lists) {
    for (const page of data?.pages ?? []) {
      const found = page.results.find((notification) => notification.id === id);
      if (found) return found;
    }
  }
  return undefined;
}

function countsQueryAppliesToBoard(
  queryKey: readonly unknown[],
  boardId: number | null | undefined,
): boolean {
  const params = (queryKey[2] ?? {}) as NotificationCountParams;
  // Account-wide counts (My Swivu): always include every board's seen changes.
  if (params.board_id == null) return true;
  if (boardId == null) return true;
  return Number(params.board_id) === Number(boardId);
}

/** Optimistically nudge unread badges; server refetch corrects facet breakdowns. */
function adjustUnreadInCounts(
  unreadDelta: number,
  boardId: number | null | undefined,
) {
  if (unreadDelta === 0) return;

  const queries = queryClient.getQueriesData<NotificationCountsResponse>({
    predicate: (query) => isCountsQuery(query.queryKey),
  });

  for (const [queryKey, old] of queries) {
    if (!old || !countsQueryAppliesToBoard(queryKey, boardId)) continue;

    const params = (queryKey[2] ?? {}) as NotificationCountParams;
    const nextUnread = Math.max(0, old.unread_count + unreadDelta);
    // Hide-seen count queries omit seen rows — marking seen shrinks their total.
    const nextTotal =
      params.is_seen === false && unreadDelta < 0
        ? Math.max(0, old.total_count + unreadDelta)
        : params.is_seen === false && unreadDelta > 0
          ? old.total_count + unreadDelta
          : old.total_count;

    queryClient.setQueryData<NotificationCountsResponse>(queryKey, {
      ...old,
      unread_count: nextUnread,
      total_count: nextTotal,
    });
  }
}

function countMatchingUnseenInLists(filters: NotificationFilters): {
  count: number;
  boardId: number | null | undefined;
} {
  const matchedIds = new Set<number>();
  let boardId: number | null | undefined = filters.board_id;

  const lists = queryClient.getQueriesData<ListData>({
    predicate: (query) => isListQuery(query.queryKey),
  });
  for (const [, data] of lists) {
    for (const page of data?.pages ?? []) {
      for (const notification of page.results) {
        if (
          !notification.is_seen &&
          notificationMatchesFilters(notification, filters) &&
          !matchedIds.has(notification.id)
        ) {
          matchedIds.add(notification.id);
          if (boardId == null) boardId = notification.board;
        }
      }
    }
  }

  return { count: matchedIds.size, boardId };
}

export function useNotifications(
  params: NotificationListParams,
  { enabled = true }: NotificationQueryOptions = {},
) {
  const queryParams = { ...params, limit: PAGE_SIZE };
  return useInfiniteQuery<
    NotificationListResponse,
    Error,
    ListData,
    ReturnType<typeof notificationKeys.list>,
    number
  >({
    queryKey: notificationKeys.list(queryParams),
    queryFn: ({ pageParam }) =>
      notificationsService.list({ ...queryParams, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, p) => sum + p.results.length, 0);
      return totalFetched < lastPage.count ? totalFetched : undefined;
    },
    enabled,
    refetchInterval: (query) =>
      listHasPending(query.state.data as ListData | undefined)
        ? PENDING_POLL_INTERVAL
        : DEFAULT_POLL_INTERVAL,
  });
}

export function useNotificationCounts(
  params: NotificationCountParams,
  { enabled = true }: NotificationQueryOptions = {},
) {
  return useQuery({
    queryKey: notificationKeys.counts(params),
    queryFn: () => notificationsService.counts(params),
    enabled,
    refetchInterval: (query) =>
      countsHavePending(query.state.data)
        ? PENDING_POLL_INTERVAL
        : DEFAULT_POLL_INTERVAL,
  });
}

function mergeNotificationIntoLists(
  id: number,
  patch: Partial<Notification> | Notification,
) {
  queryClient.setQueriesData<ListData>(
    { predicate: (query) => isListQuery(query.queryKey) },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          results: page.results.map((notification) =>
            notification.id === id
              ? { ...notification, ...patch }
              : notification,
          ),
        })),
      };
    },
  );
}

export function useMarkNotificationSeen() {
  return useMutation<
    Awaited<ReturnType<typeof notificationsService.markSeen>>,
    Error,
    MarkNotificationSeenRequest,
    { snapshots: QuerySnapshot[] }
  >({
    mutationFn: ({ id, is_seen }) => notificationsService.markSeen(id, is_seen),
    onMutate: async ({ id, is_seen }) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const snapshots = snapshotNotificationQueries();
      const existing = findNotificationInLists(id);
      const wasSeen = existing?.is_seen ?? !is_seen;
      mergeNotificationIntoLists(id, { is_seen });
      if (wasSeen !== is_seen) {
        adjustUnreadInCounts(is_seen ? -1 : 1, existing?.board);
      }
      return { snapshots };
    },
    onSuccess: (row) => {
      // Full PATCH row includes is_seen, relevancy_score, recipient_count.
      mergeNotificationIntoLists(row.id, row);
    },
    onError: (_error, _variables, context) => restoreSnapshots(context?.snapshots),
    onSettled: () => {
      // Refetch restores unread→read partition and server sort order.
      // refetchType "all" keeps My Swivu account counts in sync while on a board.
      void invalidateAllNotificationQueries();
    },
  });
}

export function useMarkAllNotificationsSeen() {
  return useMutation<
    MarkAllNotificationsSeenResponse,
    Error,
    MarkAllNotificationsSeenRequest,
    { snapshots: QuerySnapshot[] }
  >({
    mutationFn: (filters) => notificationsService.markAllSeen(filters),
    onMutate: async (filters) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const snapshots = snapshotNotificationQueries();
      const { count: unseenMatches, boardId } = countMatchingUnseenInLists(filters);

      queryClient.setQueriesData<ListData>(
        { predicate: (query) => isListQuery(query.queryKey) },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((notification) =>
                notificationMatchesFilters(notification, filters)
                  ? { ...notification, is_seen: true }
                  : notification,
              ),
            })),
          };
        },
      );
      if (unseenMatches > 0) {
        adjustUnreadInCounts(-unseenMatches, boardId ?? filters.board_id);
      }
      return { snapshots };
    },
    onError: (_error, _filters, context) => restoreSnapshots(context?.snapshots),
    onSettled: (_data, _error, filters) => {
      invalidateAfterMarkAllSeen(filters);
    },
  });
}

export function useCancelNotificationTask() {
  return useMutation({
    mutationFn: (asyncTaskId: number) => notificationsService.cancelTask(asyncTaskId),
    onSettled: () => {
      void invalidateAllNotificationQueries();
    },
  });
}

export function getNotificationTaskResult(asyncTaskId: number) {
  return notificationsService.getResult(asyncTaskId);
}
